import { useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronDown, ChevronRight, GripVertical, Move, Plus, X } from 'lucide-react';
import { listDepartments } from '@/api/departments';
import { createPositionTitle, listPositionTitles, listPositionTitlesWithRetired, retirePositionTitle, restorePositionTitle, unassignPositionTitle } from '@/api/positionTitles';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { AlertDialog } from '@/components/ui/AlertDialog';
import type { Department, PositionTitle } from '@/types/oscar';
import { geographicOptions, positionTitleSchema, type PositionTitleFormData } from './schema';
import { TITLES_KEY, useRehomeTitle } from './useRehomeTitle';

/**
 * Position Title Allocator (PR-28, refined). Left column: every department as a
 * collapsible row (collapsed by default with a linked-title counter) that is
 * also a drop target. Right column: the Unassigned tray. Drag a title chip onto
 * a department — collapsed or expanded — to re-home it (PATCH DepartmentCode).
 * Keyboard-accessible via dnd-kit's KeyboardSensor. Admin-only for
 * drag/create/retire; everyone else sees the board read-only.
 */
export function PositionTitleAllocator() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [geo, setGeo] = useState('');
  const [bu, setBu] = useState('');
  const [retireTarget, setRetireTarget] = useState<PositionTitle | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (code: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const departmentsQuery = useQuery({
    queryKey: ['departments', { includeDeleted: false }],
    queryFn: ({ signal }) => listDepartments(false, signal),
  });
  const titlesQuery = useQuery({
    queryKey: TITLES_KEY,
    queryFn: ({ signal }) => listPositionTitles(undefined, signal),
  });

  const rehome = useRehomeTitle();
  // Unassign (clear department) — optimistic, mirrors useRehomeTitle. Distinct
  // from Retire: the title stays live, just parked in the Unassigned column.
  const unassign = useMutation({
    mutationFn: ({ id }: { id: number }) => unassignPositionTitle(id),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: TITLES_KEY });
      const prev = queryClient.getQueryData<PositionTitle[]>(TITLES_KEY);
      queryClient.setQueryData<PositionTitle[]>(TITLES_KEY, (old) =>
        (old ?? []).map((t) => (t.PositionTitleId === id ? { ...t, DepartmentCode: null } : t)));
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) queryClient.setQueryData(TITLES_KEY, ctx.prev); },
    onSettled: () => queryClient.invalidateQueries({ queryKey: TITLES_KEY }),
  });

  // Retire (soft-delete). Was a bare `await` inside the dialog's onConfirm, which
  // left a rejection unhandled and closed the dialog with nothing said; it is a
  // mutation so the confirm can keep itself open and show why it failed.
  const retire = useMutation({
    mutationFn: (id: number) => retirePositionTitle(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: TITLES_KEY }); setRetireTarget(null); },
  });
  const openRetire = (t: PositionTitle) => { retire.reset(); setRetireTarget(t); };

  const departments = useMemo(() => departmentsQuery.data ?? [], [departmentsQuery.data]);
  const titles = useMemo(() => titlesQuery.data ?? [], [titlesQuery.data]);

  const buOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of departments) map.set(d.BusinessUnitCode, d.BusinessUnitDescription);
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [departments]);

  const titlesByCode = useMemo(() => {
    const map = new Map<string, PositionTitle[]>();
    for (const t of titles) {
      if (t.DepartmentCode == null) continue; // unassigned — shown in the Unassigned column
      const arr = map.get(t.DepartmentCode) ?? [];
      arr.push(t);
      map.set(t.DepartmentCode, arr);
    }
    return map;
  }, [titles]);

  const knownCodes = useMemo(() => new Set(departments.map((d) => d.DepartmentCode)), [departments]);
  // Unassigned = no department (null) OR a department that isn't in the active
  // list (e.g. references a retired code). Both surface in the Unassigned column.
  const unassignedTitles = useMemo(
    () => titles.filter((t) => t.DepartmentCode == null || !knownCodes.has(t.DepartmentCode)),
    [titles, knownCodes],
  );

  const filteredDepartments = useMemo(() => {
    const q = search.trim().toLowerCase();
    return departments.filter((d) => {
      if (geo && d.GeographicCode !== geo) return false;
      if (bu && d.BusinessUnitCode !== bu) return false;
      if (q && !(`${d.DepartmentCode} ${d.DepartmentDescription}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [departments, search, geo, bu]);

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const onDragEnd = (e: DragEndEvent) => {
    const over = e.over?.data.current as { code?: string; unassign?: boolean } | undefined;
    const active = e.active.data.current as { id: number; fromCode: string | null } | undefined;
    if (!active) return;
    if (over?.unassign) {
      if (active.fromCode == null) return; // already unassigned — no-op
      unassign.mutate({ id: active.id });
      return;
    }
    const toCode = over?.code;
    if (!toCode || toCode === active.fromCode) return;
    rehome.mutate({ id: active.id, toCode });
  };

  const loading = departmentsQuery.isLoading || titlesQuery.isLoading;
  const errored = departmentsQuery.isError || titlesQuery.isError;

  const expandAll = () => setExpanded(new Set(filteredDepartments.map((d) => d.DepartmentCode)));
  const collapseAll = () => setExpanded(new Set());

  return (
    <section>
      <header className="mb-5 flex items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-navy">
            <Move className="h-6 w-6 text-navy" aria-hidden /> Position Title Allocator
          </h1>
          <p className="mt-1 text-sm text-darkGrey/80">
            {isAdmin
              ? 'Expand a department to see its titles. Drag a title onto another department (collapsed or open) to re-home it. Keyboard: focus a title, press Space, arrow to a department, Space to drop.'
              : 'Read-only view of which department each position title belongs to.'}
          </p>
        </div>
      </header>

      {(rehome.isError || unassign.isError) && (
        <div role="alert" className="mb-3 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">
          {rehome.error instanceof ApiError ? rehome.error.message
            : unassign.error instanceof ApiError ? unassign.error.message
            : 'Could not move that position title.'} The title was returned to where it was.
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Geographic
          <select
            aria-label="Filter by geographic scope"
            value={geo}
            onChange={(e) => setGeo(e.target.value)}
            className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal"
          >
            <option value="">All</option>
            {geographicOptions.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Business unit
          <select
            aria-label="Filter by business unit"
            value={bu}
            onChange={(e) => setBu(e.target.value)}
            className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal"
          >
            <option value="">All</option>
            {buOptions.map(([code, desc]) => <option key={code} value={code}>{desc} ({code})</option>)}
          </select>
        </label>
        <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
          Search departments
          <input
            aria-label="Search departments"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Code or description"
            className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal"
          />
        </label>
        <div className="flex items-center gap-2 pb-0.5 text-xs">
          <button type="button" onClick={expandAll} className="rounded border border-border bg-white px-2 py-1.5 font-medium text-darkGrey hover:bg-bg">Expand all</button>
          <button type="button" onClick={collapseAll} className="rounded border border-border bg-white px-2 py-1.5 font-medium text-darkGrey hover:bg-bg">Collapse all</button>
        </div>
      </div>

      {isAdmin && <AddTitlePanel departments={departments} onCreated={() => queryClient.invalidateQueries({ queryKey: TITLES_KEY })} />}

      {loading && <p className="mt-6 text-sm text-darkGrey/60">Loading…</p>}
      {errored && <p className="mt-6 text-sm text-red">Could not load the allocator.</p>}

      {!loading && !errored && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-start">
            {/* Left: department accordion (drop targets). */}
            <div className="flex-1 space-y-2">
              {filteredDepartments.map((d) => (
                <DepartmentRow
                  key={d.DepartmentCode}
                  dept={d}
                  titles={titlesByCode.get(d.DepartmentCode) ?? []}
                  admin={isAdmin}
                  expanded={expanded.has(d.DepartmentCode)}
                  onToggle={() => toggle(d.DepartmentCode)}
                  onRetireTitle={openRetire}
                />
              ))}
              {filteredDepartments.length === 0 && (
                <p className="text-sm text-darkGrey/60">No departments match the filters.</p>
              )}
            </div>

            {/* Right: Unassigned column (drop target) + Retired tray. */}
            <aside className="w-full shrink-0 space-y-3 lg:sticky lg:top-4 lg:w-72">
              <UnassignedColumn titles={unassignedTitles} admin={isAdmin} onRetireTitle={openRetire} />
              {isAdmin && <RetiredTitlesPanel />}
            </aside>
          </div>
        </DndContext>
      )}

      {retireTarget && (
        <AlertDialog
          open={retireTarget !== null}
          onOpenChange={(o) => { if (!o) setRetireTarget(null); }}
          title={`Retire "${retireTarget.PositionTitle}"?`}
          description="The position title is soft-deleted and removed from the board."
          confirmLabel="Retire"
          destructive
          closeOnConfirm={false}
          onConfirm={() => retire.mutate(retireTarget.PositionTitleId)}
        >
          {retire.isError && (
            <p role="alert" className="text-sm text-red">
              {retire.error instanceof ApiError ? retire.error.message : 'Could not retire that title.'}
            </p>
          )}
        </AlertDialog>
      )}
    </section>
  );
}

// ── Department row (collapsible + droppable) ────────────────────────────────

function DepartmentRow({
  dept,
  titles,
  admin,
  expanded,
  onToggle,
  onRetireTitle,
}: {
  dept: Department;
  titles: PositionTitle[];
  admin: boolean;
  expanded: boolean;
  onToggle: () => void;
  onRetireTitle: (t: PositionTitle) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `dept-${dept.DepartmentCode}`, data: { code: dept.DepartmentCode } });
  const Chevron = expanded ? ChevronDown : ChevronRight;
  return (
    <div
      ref={setNodeRef}
      className={`rounded border bg-white shadow-sm transition-colors ${isOver ? 'border-orange ring-2 ring-orange/40' : 'border-border'}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-bg/50"
      >
        <Chevron className="h-4 w-4 shrink-0 text-darkGrey/50" aria-hidden />
        <span className="font-mono text-sm font-semibold text-navy">{dept.DepartmentCode}</span>
        <span className="truncate text-xs text-darkGrey/70">{dept.DepartmentDescription}</span>
        <span className="ml-auto shrink-0 rounded-full bg-bg px-2 py-0.5 text-xs font-semibold text-darkGrey/70">
          {titles.length}
        </span>
      </button>
      {expanded && (
        <ul className="space-y-1.5 border-t border-border px-3 py-2">
          {titles.map((t) => (
            <TitleChip key={t.PositionTitleId} title={t} admin={admin} onRetire={onRetireTitle} />
          ))}
          {titles.length === 0 && <li className="text-xs text-darkGrey/50">No position titles.</li>}
        </ul>
      )}
    </div>
  );
}

// ── Unassigned column (drop target) ─────────────────────────────────────────

function UnassignedColumn({ titles, admin, onRetireTitle }: {
  titles: PositionTitle[];
  admin: boolean;
  onRetireTitle: (t: PositionTitle) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: 'unassigned-drop', data: { unassign: true } });
  return (
    <div
      ref={setNodeRef}
      className={`rounded border border-dashed p-3 transition-colors ${isOver ? 'border-orange bg-orange/5 ring-2 ring-orange/40' : 'border-border bg-bg/40'}`}
    >
      <h2 className="text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
        Unassigned <span className="text-darkGrey/40">({titles.length})</span>
      </h2>
      {admin && <p className="mt-0.5 text-xs text-darkGrey/50">Drag a title here to unassign it — it stays live, just with no department.</p>}
      {titles.length === 0 ? (
        <p className="mt-1 text-sm text-darkGrey/60">No unassigned position titles.</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {titles.map((t) => (
            <TitleChip key={t.PositionTitleId} title={t} admin={admin} onRetire={onRetireTitle} />
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Retired titles tray (admin — restore) ───────────────────────────────────

function RetiredTitlesPanel() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const query = useQuery({
    queryKey: ['positionTitles', { includeDeleted: true }],
    queryFn: ({ signal }) => listPositionTitlesWithRetired(signal),
    enabled: open,
  });
  const retired = (query.data ?? []).filter((t) => t.IsDeleted);
  const restore = useMutation({
    mutationFn: (id: number) => restorePositionTitle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TITLES_KEY });
      queryClient.invalidateQueries({ queryKey: ['positionTitles', { includeDeleted: true }] });
    },
  });

  return (
    <div className="rounded border border-border bg-white p-3 shadow-sm">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex w-full items-center gap-2 text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
        {open ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
        Retired titles
      </button>
      {open && (
        <div className="mt-2">
          {query.isLoading && <p className="text-sm text-darkGrey/60">Loading…</p>}
          {query.isError && <p className="text-sm text-red">Could not load retired titles.</p>}
          {!query.isLoading && !query.isError && retired.length === 0 && (
            <p className="text-sm text-darkGrey/60">No retired position titles.</p>
          )}
          {restore.isError && <p role="alert" className="mb-1 text-xs text-red">{restore.error instanceof ApiError ? restore.error.message : 'Could not restore that title.'}</p>}
          <ul className="space-y-1.5">
            {retired.map((t) => (
              <li key={t.PositionTitleId} className="flex items-center justify-between gap-2 rounded border border-border bg-bg/40 px-2 py-1 text-sm">
                <span className="truncate text-darkGrey/70" title={t.PositionTitle}>{t.PositionTitle}</span>
                <button
                  type="button"
                  onClick={() => restore.mutate(t.PositionTitleId)}
                  disabled={restore.isPending}
                  className="shrink-0 rounded border border-border bg-white px-2 py-0.5 text-xs font-medium text-navy hover:bg-bg disabled:opacity-50"
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Title chip (draggable) ──────────────────────────────────────────────────

function TitleChip({
  title,
  admin,
  onRetire,
}: {
  title: PositionTitle;
  admin: boolean;
  onRetire: (t: PositionTitle) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `title-${title.PositionTitleId}`,
    data: { id: title.PositionTitleId, fromCode: title.DepartmentCode },
    disabled: !admin,
  });
  const style = transform ? { transform: CSS.Translate.toString(transform) } : undefined;

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 rounded border border-border bg-bg/50 px-2 py-1 text-sm ${isDragging ? 'opacity-50' : ''}`}
    >
      {admin && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`Drag ${title.PositionTitle}`}
          className="cursor-grab touch-none text-darkGrey/40 hover:text-darkGrey focus:outline-none focus-visible:ring-2 focus-visible:ring-navy"
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </button>
      )}
      <span className="flex-1 text-darkGrey/90">{title.PositionTitle}</span>
      {admin && (
        <button
          type="button"
          aria-label={`Retire ${title.PositionTitle}`}
          onClick={() => onRetire(title)}
          className="text-darkGrey/40 hover:text-red focus:outline-none focus-visible:ring-2 focus-visible:ring-red"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
    </li>
  );
}

// ── Add position title (admin) ──────────────────────────────────────────────

function AddTitlePanel({ departments, onCreated }: { departments: Department[]; onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<PositionTitleFormData>({
    resolver: zodResolver(positionTitleSchema),
    defaultValues: { PositionTitle: '', DepartmentCode: '' },
  });
  const { register, handleSubmit, reset, formState } = form;

  const mutation = useMutation({
    mutationFn: (data: PositionTitleFormData) => createPositionTitle(data),
    onSuccess: () => {
      onCreated();
      reset({ PositionTitle: '', DepartmentCode: '' });
      setSubmitError(null);
      setOpen(false);
    },
    onError: (err) => setSubmitError(err instanceof ApiError ? err.message : 'Could not add the position title.'),
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded bg-orange px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
      >
        <Plus className="h-4 w-4" aria-hidden /> Add position title
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit((d) => { setSubmitError(null); mutation.mutate(d); })}
      className="rounded border border-border bg-white p-3 shadow-sm"
    >
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
          Position title
          <input {...register('PositionTitle')} className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal" />
          {formState.errors.PositionTitle && <span className="mt-1 font-normal text-red">{formState.errors.PositionTitle.message}</span>}
        </label>
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" aria-hidden /> Department</span>
          <select {...register('DepartmentCode')} className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal">
            <option value="">Choose…</option>
            {departments.map((d) => (
              <option key={d.DepartmentCode} value={d.DepartmentCode}>{d.DepartmentCode} — {d.DepartmentDescription}</option>
            ))}
          </select>
          {formState.errors.DepartmentCode && <span className="mt-1 font-normal text-red">{formState.errors.DepartmentCode.message}</span>}
        </label>
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded bg-orange px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {mutation.isPending ? 'Adding…' : 'Add'}
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); setSubmitError(null); reset({ PositionTitle: '', DepartmentCode: '' }); }}
            className="rounded border border-border bg-white px-3 py-2 text-sm font-medium text-darkGrey hover:bg-bg"
          >
            Cancel
          </button>
        </div>
      </div>
      {submitError && <p role="alert" className="mt-2 text-sm text-red">{submitError}</p>}
    </form>
  );
}
